import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell,
} from "recharts";
import {
  Users, Briefcase, Store, Car, Crown, Wallet, Star, TrendingUp, TrendingDown,
  CheckCircle2, AlertTriangle, UserMinus, Sparkles, CalendarDays, ArrowUpRight,
  Clock, MapPin, Check, X, Bell, MessageCircle, Lock, Trophy, Percent, BarChart3, ChevronDown,
  Loader2, RefreshCw,
} from "lucide-react";
import { fetchWithToken, patchWithToken, deleteWithToken } from "api/ApiHandler";
import { handleAuthError } from "../userManagement/shared";
import Sidebar1 from "components/Sidebar1";
import BookingDialog from "./BookingDialog";
import ShopDialog from "./ShopDialog";
import { Avatar, StatusBadge, VisitsBadge, STATUS_DOTS } from "./ui";
import {
  BRAND, FREE_VEHICLE_LIMIT, BOOKING_STATUSES, EMPTY_STATUS_COUNTS, PLAN_COLORS, FREE_SPLIT_COLOR,
  colorAt, carLabel, normalizeBooking, normalizeShop,
} from "./dashboardData";

const FONT_HEADING = { fontFamily: "'NeueMontreal', sans-serif" };

const KPI_ICONS = {
  customers: Users,
  providers: Briefcase,
  shops: Store,
  vehicles: Car,
  subscribers: Crown,
  revenue: Wallet,
};

const PERIODS = ["7D", "30D", "12M", "All"];
const PERIOD_LABELS = { "7D": "7D", "30D": "30D", "12M": "12M", All: "All time" };
const BOOKINGS_PREVIEW = 5;

// "Shop 4, Marina Plaza, Dubai Marina, Dubai" → "Dubai Marina"
const shortAddress = (address = "") => {
  const parts = address.split(",").map((p) => p.trim()).filter(Boolean);
  return parts.length > 1 ? parts[parts.length - 2] : parts[0] || "—";
};
const formatAED = (n) => `AED ${Number(n).toLocaleString()}`;
const formatCompact = (n) => (n >= 1000 ? `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k` : n);

export default function AdmindashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadOverview = useCallback(async () => {
    setLoading(true);
    try {
      setData(await fetchWithToken("/admin/dashboard-overview"));
    } catch (error) {
      console.error("Dashboard overview failed:", error.response?.data || error);
      handleAuthError(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  const today = new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  return (
    <div className="flex min-h-screen bg-[#F6F6F8]">
      <Helmet>
        <title>Fuelingo | Dashboard</title>
      </Helmet>

      <Sidebar1 />
      <ToastContainer position="top-right" autoClose={2500} hideProgressBar />

      <main className="flex-1 min-w-0 h-screen overflow-y-auto px-8 py-6 lg:px-6 md:px-4 sm:px-3">
        {/* Header */}
        <header className="flex items-center justify-between gap-4 mb-7 md:flex-col md:items-start">
          <div>
            <h1 className="m-0 text-3xl sm:text-2xl font-medium text-[#111]" style={FONT_HEADING}>Dashboard</h1>
            <p className="m-0 mt-1 text-sm text-[#6B7280]">
              Welcome back, Admin — here’s what’s happening across customers, providers and shops.
            </p>
          </div>
          <div className="flex items-center gap-3 md:w-full md:justify-between">
            <div className="flex items-center gap-2 rounded-xl bg-[#fff] border border-[#ECECEF] px-3 py-2 text-xs text-[#4B5563] sm:hidden">
              <CalendarDays size={15} className="text-[#EA352B]" />
              {today}
            </div>
            <button
              type="button"
              aria-label="Refresh dashboard"
              title="Refresh"
              onClick={loadOverview}
              disabled={loading}
              className="w-10 h-10 rounded-xl bg-[#fff] border border-[#ECECEF] hover:border-[#EA352B] flex items-center justify-center transition disabled:opacity-60"
            >
              <RefreshCw size={16} className={`text-[#374151] ${loading ? "animate-spin" : ""}`} />
            </button>
            <NotificationBell items={data ? data.recentActivity : []} />
          </div>
        </header>

        {data ? (
          <DashboardBody data={data} />
        ) : loading ? (
          <div className="flex items-center justify-center gap-2 py-32 text-sm text-[#6B7280]">
            <Loader2 size={18} className="animate-spin text-[#EA352B]" /> Loading dashboard…
          </div>
        ) : (
          <div className="mx-auto max-w-md rounded-3xl bg-[#fff] border border-[#EEEEF1] p-8 text-center">
            <AlertTriangle size={26} className="mx-auto text-[#B45309]" />
            <p className="m-0 mt-3 text-base font-medium text-[#111]" style={FONT_HEADING}>Couldn’t load the dashboard</p>
            <p className="m-0 mt-1 text-sm text-[#6B7280]">Check your connection and try again.</p>
            <button type="button" onClick={loadOverview} className="mt-5 rounded-xl bg-[#EA352B] px-5 py-2.5 text-sm font-semibold text-[#fff] hover:bg-[#C92A21] transition">
              Retry
            </button>
          </div>
        )}
      </main>
    </div>
  );
}

function DashboardBody({ data }) {
  const [period, setPeriod] = useState("12M");
  const [revenueSeries, setRevenueSeries] = useState({ services: true, subscriptions: true });
  const [growthSeries, setGrowthSeries] = useState({ customers: true, providers: true });
  const [bookingFilter, setBookingFilter] = useState("All");
  const [showAllBookings, setShowAllBookings] = useState(false);
  const [approvals, setApprovals] = useState(data.pendingApprovals.items);
  const [busyApproval, setBusyApproval] = useState(null);
  const [openBooking, setOpenBooking] = useState(null);
  const closeBooking = useCallback(() => setOpenBooking(null), []);
  const [openShop, setOpenShop] = useState(null);
  const closeShop = useCallback(() => setOpenShop(null), []);

  const { kpis, insight, subscriptions, userGrowth } = data;
  const freeLimit = insight.freeVehicleLimit ?? FREE_VEHICLE_LIMIT;
  const recentBookings = useMemo(() => data.chatBookings.items.map(normalizeBooking), [data]);
  const topShops = useMemo(() => data.topShops.map(normalizeShop), [data]);
  const bookingStatusCounts = { ...EMPTY_STATUS_COUNTS, ...data.chatBookings.statusCounts };
  const vehiclesPerCustomer = data.vehiclesPerCustomer.items;
  const vehicleTypes = data.vehicleTypes.items.map((v, i) => ({ ...v, color: colorAt(i) }));
  const serviceCategories = data.jobsByService.items.map((c, i) => ({ ...c, color: colorAt(i) }));
  const split = subscriptions.split.map((s, i) => ({ ...s, color: i === 0 ? BRAND.red : FREE_SPLIT_COLOR }));

  const filteredBookings = bookingFilter === "All"
    ? recentBookings
    : recentBookings.filter((b) => b.status === bookingFilter);
  const visibleBookings = showAllBookings ? filteredBookings : filteredBookings.slice(0, BOOKINGS_PREVIEW);

  const revenue = data.revenueByPeriod[period];
  const revenueData = revenue.series;
  const { summary } = revenue;

  // Keep at least one series visible so the chart never goes blank.
  const toggleSeries = (setter) => (key) =>
    setter((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      return Object.values(next).some(Boolean) ? next : prev;
    });

  const handleApproval = async (provider, approved) => {
    if (!approved && !window.confirm(`Reject ${provider.name}? Their application will be permanently deleted.`)) return;
    setBusyApproval(provider.id);
    try {
      if (approved) await patchWithToken(`/admin/approve-service-provider/${provider.id}`);
      else await deleteWithToken(`/admin/reject-service-provider/${provider.id}`);
      setApprovals((list) => list.filter((p) => p.id !== provider.id));
      if (approved) toast.success(`${provider.name} approved`);
      else toast.info(`${provider.name} rejected`);
    } catch (error) {
      // The API client already shows an error toast.
      handleAuthError(error);
    } finally {
      setBusyApproval(null);
    }
  };

  return (
    <>
    {/* Hero banner */}
    <section
      className="relative overflow-hidden rounded-3xl p-7 mb-6 md:p-5 text-[#fff]"
      style={{ background: `linear-gradient(120deg, ${BRAND.dark} 0%, #2A1614 55%, ${BRAND.red} 130%)` }}
    >
      <div className="absolute -right-16 -top-16 w-64 h-64 rounded-full opacity-20" style={{ background: BRAND.red }} />
      <div className="absolute right-24 -bottom-20 w-52 h-52 rounded-full opacity-10" style={{ background: BRAND.orange }} />
      <div className="relative flex items-center justify-between gap-6 md:flex-col md:items-start">
        <div className="max-w-[560px]">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#ffffff1f] px-3 py-1 text-[11px] font-medium tracking-wide">
            <Sparkles size={13} /> Subscription insight
          </span>
          <h2 className="m-0 mt-3 text-2xl sm:text-xl font-medium leading-snug" style={FONT_HEADING}>
            {insight.convertedAfterThirdVehicle > 0
              ? `${insight.convertedAfterThirdVehicle.toLocaleString()} customer${insight.convertedAfterThirdVehicle === 1 ? "" : "s"} added a 3rd vehicle and converted to subscribers this quarter`
              : "No customers have upgraded after adding a 3rd vehicle this quarter yet"}
          </h2>
          <p className="m-0 mt-2 text-sm text-[#ffffffb3]">
            Customers can add up to {freeLimit} vehicles for free. {insight.atLimitCustomers.toLocaleString()} customers
            are sitting at the limit — your biggest upgrade opportunity.
          </p>
        </div>
        <div className="grid grid-cols-3 gap-3 sm:w-full sm:gap-2">
          <HeroStat label="MRR" value={subscriptions.mrrLabel} />
          <HeroStat label="Conversion" value={`${subscriptions.conversionRate}%`} />
          <HeroStat label="Churn" value={`${subscriptions.churnRate}%`} />
        </div>
      </div>
    </section>

    {/* KPI cards */}
    <section className="grid grid-cols-6 lg:grid-cols-3 md:grid-cols-2 sm:grid-cols-1 gap-5 md:gap-4 mb-6">
      {kpis.map((k) => (
        <StatCard key={k.key} label={k.label} value={k.display} change={k.change} spark={k.spark} to={k.to} Icon={KPI_ICONS[k.key]} />
      ))}
    </section>

    {/* Revenue + Subscriptions */}
    <section className="grid grid-cols-3 md:grid-cols-1 gap-6 md:gap-4 mb-6">
      <Card className="col-span-2 md:col-span-1">
        <div className="flex items-start justify-between gap-4 mb-4 sm:flex-col">
          <div>
            <CardTitle>Revenue Overview</CardTitle>
            <p className="m-0 mt-0.5 text-xs text-[#9CA3AF]">{revenue.label}</p>
            <div className="flex items-baseline gap-2 mt-2 flex-wrap">
              <span className="text-2xl font-semibold text-[#111]">{formatAED(revenue.total)}</span>
              {revenue.growthPct !== null ? (
                <>
                  <TrendPill value={revenue.growthPct} />
                  <span className="text-[11px] text-[#9CA3AF]">vs previous period</span>
                </>
              ) : (
                <span className="text-[11px] font-semibold rounded-full px-2 py-0.5 bg-[#FDECEA] text-[#EA352B]">
                  Lifetime revenue
                </span>
              )}
            </div>
          </div>
          <Segmented options={PERIODS} labels={PERIOD_LABELS} value={period} onChange={setPeriod} />
        </div>

        <div className="flex gap-2 flex-wrap mb-4">
          <SeriesToggle
            label="Services"
            value={formatAED(revenue.servicesTotal)}
            color={BRAND.red}
            active={revenueSeries.services}
            onClick={() => toggleSeries(setRevenueSeries)("services")}
          />
          <SeriesToggle
            label="Subscriptions"
            value={formatAED(revenue.subscriptionsTotal)}
            color={BRAND.orange}
            active={revenueSeries.subscriptions}
            onClick={() => toggleSeries(setRevenueSeries)("subscriptions")}
          />
        </div>

        <div className="h-[280px] sm:h-[230px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={revenueData} margin={{ top: 5, right: 5, left: -10, bottom: 0 }}>
              <defs>
                <linearGradient id="gradServices" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={BRAND.red} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={BRAND.red} stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gradSubs" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={BRAND.orange} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={BRAND.orange} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="4 4" vertical={false} stroke="#EEEEF1" />
              <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: "#9CA3AF", fontSize: 12 }} dy={8} interval="preserveStartEnd" minTickGap={12} />
              <YAxis axisLine={false} tickLine={false} tick={{ fill: "#9CA3AF", fontSize: 12 }} tickFormatter={formatCompact} />
              <Tooltip content={<ChartTooltip money showTotal />} />
              {revenueSeries.services && (
                <Area type="monotone" dataKey="services" name="Services" stroke={BRAND.red} strokeWidth={2.5} fill="url(#gradServices)" />
              )}
              {revenueSeries.subscriptions && (
                <Area type="monotone" dataKey="subscriptions" name="Subscriptions" stroke={BRAND.orange} strokeWidth={2.5} fill="url(#gradSubs)" />
              )}
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="grid grid-cols-4 sm:grid-cols-2 gap-3 sm:gap-2 mt-5">
          <MiniStat Icon={BarChart3} label={`Avg per ${revenue.unit}`} value={formatAED(summary.avgPerUnit)} />
          <MiniStat Icon={Trophy} label={`Best ${revenue.unit}`} value={formatAED(summary.bestPeriod.revenue)} sub={summary.bestPeriod.name} />
          <MiniStat Icon={Percent} label="From subscriptions" value={`${summary.fromSubscriptionsPct}%`} />
          <MiniStat
            Icon={summary.growthFirstToLastPct === null || summary.growthFirstToLastPct >= 0 ? TrendingUp : TrendingDown}
            label={`Growth, first → last ${revenue.unit}`}
            value={summary.growthFirstToLastPct === null
              ? "—"
              : `${summary.growthFirstToLastPct >= 0 ? "+" : ""}${summary.growthFirstToLastPct}%`}
          />
        </div>
      </Card>

      <Card>
        <div className="flex items-center justify-between">
          <CardTitle>Subscriptions</CardTitle>
          <IconBadge Icon={Crown} />
        </div>
        <div className="h-[190px] relative mt-2">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={split}
                innerRadius={62}
                outerRadius={85}
                startAngle={90}
                endAngle={-270}
                paddingAngle={2}
                dataKey="value"
                stroke="none"
              >
                {split.map((s) => <Cell key={s.name} fill={s.color} />)}
              </Pie>
              <Tooltip content={<ChartTooltip />} />
            </PieChart>
          </ResponsiveContainer>
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <span className="text-2xl font-semibold text-[#111]">{subscriptions.conversionRate}%</span>
            <span className="text-[11px] text-[#9CA3AF]">customers subscribed</span>
          </div>
        </div>
        <div className="flex justify-center gap-4 text-xs text-[#6B7280] mb-4 flex-wrap">
          {split.map((s) => (
            <LegendDot key={s.name} color={s.color} label={`${s.name} · ${s.value.toLocaleString()}`} />
          ))}
        </div>
        <div className="space-y-2">
          {subscriptions.plans.map((p, i) => (
            <div key={p.name} className="flex items-center justify-between rounded-xl bg-[#F7F7F9] px-3 py-2.5">
              <div className="flex items-center gap-2.5">
                <span className="w-2 h-8 rounded-full" style={{ background: PLAN_COLORS[i] }} />
                <div>
                  <p className="m-0 text-sm font-medium text-[#111]">{p.name}</p>
                  <p className="m-0 text-[11px] text-[#9CA3AF]">{p.priceLabel}</p>
                </div>
              </div>
              <span className="text-sm font-semibold text-[#111]">{p.subscribers.toLocaleString()}</span>
            </div>
          ))}
        </div>
      </Card>
    </section>

    {/* Vehicles + Growth */}
    <section className="grid grid-cols-3 lg:grid-cols-2 md:grid-cols-1 gap-6 md:gap-4 mb-6">
      <Card>
        <div className="flex items-center justify-between">
          <CardTitle>Vehicles per Customer</CardTitle>
          <IconBadge Icon={Car} />
        </div>
        <p className="m-0 mt-1 text-xs text-[#6B7280]">
          More than {freeLimit} vehicles requires a subscription.
        </p>
        <div className="h-[210px] mt-4">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={vehiclesPerCustomer} margin={{ top: 5, right: 5, left: -15, bottom: 0 }}>
              <CartesianGrid strokeDasharray="4 4" vertical={false} stroke="#EEEEF1" />
              <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: "#9CA3AF", fontSize: 12 }} />
              <YAxis axisLine={false} tickLine={false} tick={{ fill: "#9CA3AF", fontSize: 12 }} tickFormatter={formatCompact} />
              <Tooltip cursor={{ fill: "#F7F7F9" }} content={<ChartTooltip suffix=" customers" />} />
              <Bar dataKey="customers" name="Customers" radius={[8, 8, 0, 0]} maxBarSize={42}>
                {vehiclesPerCustomer.map((d) => (
                  <Cell key={d.name} fill={d.tier === "free" ? "#D9DAE0" : BRAND.red} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="flex gap-4 text-xs text-[#6B7280] mt-3 flex-wrap">
          <LegendDot color="#D9DAE0" label="Free tier" />
          <LegendDot color={BRAND.red} label="Subscription required" />
        </div>
      </Card>

      <Card>
        <div className="flex items-center justify-between">
          <CardTitle>Vehicle Types</CardTitle>
          <span className="text-xs text-[#6B7280]">
            {data.vehicleTypes.total.toLocaleString()} total
          </span>
        </div>
        <div className="mt-5 space-y-4">
          {vehicleTypes.map((v) => (
            <ProgressRow
              key={v.name}
              label={v.name}
              value={v.count}
              max={vehicleTypes[0]?.count}
              color={v.color}
            />
          ))}
        </div>
      </Card>

      <Card className="lg:col-span-2 md:col-span-1">
        <div className="flex items-center justify-between">
          <CardTitle>User Growth</CardTitle>
          <TrendPill value={userGrowth.growthPct} />
        </div>
        <p className="m-0 mt-1 text-xs text-[#6B7280]">New customers vs. new providers per month · tap to show / hide</p>
        <div className="flex gap-2 flex-wrap mt-3">
          <SeriesToggle
            label="Customers"
            value={`+${userGrowth.totalCustomers.toLocaleString()}`}
            color={BRAND.red}
            active={growthSeries.customers}
            onClick={() => toggleSeries(setGrowthSeries)("customers")}
          />
          <SeriesToggle
            label="Providers"
            value={`+${userGrowth.totalProviders.toLocaleString()}`}
            color={BRAND.dark}
            active={growthSeries.providers}
            onClick={() => toggleSeries(setGrowthSeries)("providers")}
          />
        </div>
        <div className="h-[230px] mt-4">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={userGrowth.items} barGap={4} margin={{ top: 5, right: 0, left: -15, bottom: 0 }}>
              <CartesianGrid strokeDasharray="4 4" vertical={false} stroke="#EEEEF1" />
              <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: "#9CA3AF", fontSize: 12 }} />
              {/* Providers are ~10x fewer than customers, so they get their own axis */}
              <YAxis yAxisId="customers" hide={!growthSeries.customers} axisLine={false} tickLine={false} tick={{ fill: BRAND.red, fontSize: 11 }} tickFormatter={formatCompact} />
              <YAxis yAxisId="providers" orientation="right" hide={!growthSeries.providers} axisLine={false} tickLine={false} tick={{ fill: BRAND.dark, fontSize: 11 }} width={35} />
              <Tooltip cursor={{ fill: "#F7F7F9" }} content={<ChartTooltip />} />
              {growthSeries.customers && (
                <Bar yAxisId="customers" dataKey="customers" name="Customers" fill={BRAND.red} radius={[6, 6, 0, 0]} maxBarSize={22} />
              )}
              {growthSeries.providers && (
                <Bar yAxisId="providers" dataKey="providers" name="Providers" fill={BRAND.dark} radius={[6, 6, 0, 0]} maxBarSize={22} />
              )}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>
    </section>

    {/* Top shops + service categories */}
    <section className="grid grid-cols-3 md:grid-cols-1 gap-6 md:gap-4 mb-6">
      <Card className="col-span-2 md:col-span-1">
        <div className="flex items-center justify-between mb-4">
          <div>
            <CardTitle>Top Provider Shops</CardTitle>
            <p className="m-0 mt-1 text-xs text-[#6B7280]">Click a shop to see its services, timing, location and links</p>
          </div>
          <LinkButton label="View all" to="/user-management/service-provider" />
        </div>
        <TableWrap>
          <table className="w-full min-w-[680px] text-left border-separate border-spacing-0">
            <thead>
              <tr>
                {["Shop", "Location", "Services", "Jobs", "Rating", "Revenue", "Status"].map((h) => (
                  <Th key={h}>{h}</Th>
                ))}
              </tr>
            </thead>
            <tbody>
              {topShops.map((s, i) => (
                <tr
                  key={s._id}
                  tabIndex={0}
                  role="button"
                  aria-label={`Open shop ${s.shopName}`}
                  onClick={() => setOpenShop(s)}
                  onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), setOpenShop(s))}
                  className="cursor-pointer hover:bg-[#FFF7F6] focus:outline-none focus-visible:bg-[#FFF7F6] transition-colors"
                >
                  <Td>
                    <div className="flex items-center gap-3">
                      <Avatar name={s.shopName} rank={i + 1} />
                      <div>
                        <p className="m-0 text-sm font-medium text-[#111]">{s.shopName}</p>
                        <p className="m-0 text-[11px] text-[#9CA3AF]">{s.provider.name}</p>
                      </div>
                    </div>
                  </Td>
                  <Td>
                    <span className="inline-flex items-center gap-1 text-[#6B7280]">
                      <MapPin size={12} /> {shortAddress(s.locations[0]?.address)}
                    </span>
                  </Td>
                  <Td>
                    {s.services.length}
                    <span className="block text-[11px] text-[#9CA3AF]">
                      {new Set(s.services.map((x) => x.serviceName)).size} categories
                    </span>
                  </Td>
                  <Td className="font-medium text-[#111]">{s.jobs}</Td>
                  <Td>
                    <span className="inline-flex items-center gap-1 font-medium text-[#111]">
                      <Star size={13} className="text-[#F5B400] fill-[#F5B400]" /> {s.rating}
                    </span>
                  </Td>
                  <Td className="font-medium text-[#111]">{s.revenueLabel}</Td>
                  <Td><StatusBadge status={s.status} /></Td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
      </Card>

      <Card>
        <div className="flex items-center justify-between">
          <CardTitle>Jobs by Service</CardTitle>
          <span className="text-xs text-[#6B7280]">
            {data.jobsByService.total.toLocaleString()} jobs
          </span>
        </div>
        <div className="mt-5 space-y-4">
          {serviceCategories.map((c) => (
            <ProgressRow key={c.name} label={c.name} value={c.jobs} max={serviceCategories[0]?.jobs} color={c.color} />
          ))}
        </div>
      </Card>
    </section>

    {/* Recent bookings + side panels */}
    <section className="grid grid-cols-3 md:grid-cols-1 gap-6 md:gap-4 mb-2">
      <Card className="col-span-2 md:col-span-1">
        <div className="flex items-start justify-between gap-3 mb-1">
          <div>
            <CardTitle>Chat Bookings</CardTitle>
            <p className="m-0 mt-1 text-xs text-[#6B7280]">
              Customer opens a chat → provider approves &amp; chats → marks it completed or rejects it · click a row for details
            </p>
          </div>
          {bookingFilter !== "All" && (
            <button type="button" onClick={() => setBookingFilter("All")} className="shrink-0 text-xs font-semibold text-[#EA352B]">
              Clear filter
            </button>
          )}
        </div>

        {/* Status summary (doubles as filter) */}
        <div className="grid grid-cols-4 sm:grid-cols-2 gap-3 sm:gap-2 my-4">
          {BOOKING_STATUSES.map((st) => (
            <button
              key={st}
              type="button"
              onClick={() => setBookingFilter(bookingFilter === st ? "All" : st)}
              className={`text-left rounded-2xl border p-3 transition-all ${
                bookingFilter === st ? "border-[#EA352B] bg-[#FFF7F6]" : "border-[#EEEEF1] bg-[#F7F7F9] hover:border-[#D9DAE0]"
              }`}
            >
              <span className="flex items-center gap-1.5 text-[11px] font-medium text-[#6B7280]">
                <span className="w-2 h-2 rounded-full" style={{ background: STATUS_DOTS[st] }} />
                {st}
              </span>
              <span className="block mt-1 text-lg font-semibold text-[#111]">
                {bookingStatusCounts[st].toLocaleString()}
              </span>
            </button>
          ))}
        </div>

        <TableWrap>
          <table className="w-full min-w-[820px] text-left border-separate border-spacing-0">
            <thead>
              <tr>
                {["Chat", "Customer", "Service", "Shop", "Visits to shop", "Last update", "Status"].map((h) => (
                  <Th key={h}>{h}</Th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibleBookings.map((b) => (
                <tr
                  key={b.id}
                  tabIndex={0}
                  role="button"
                  aria-label={`Open chat ${b.id}`}
                  onClick={() => setOpenBooking(b)}
                  onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), setOpenBooking(b))}
                  className="cursor-pointer hover:bg-[#FFF7F6] focus:outline-none focus-visible:bg-[#FFF7F6] transition-colors"
                >
                  <Td>
                    <span className="inline-flex items-center gap-1.5 font-medium text-[#111]">
                      {b.status === "Pending"
                        ? <Lock size={13} className="text-[#B45309]" />
                        : <MessageCircle size={14} className="text-[#EA352B]" />}
                      {b.id}
                    </span>
                    <span className="block text-[11px] text-[#9CA3AF]">
                      {b.status === "Pending" ? "Locked" : `${b.messagesCount} messages`}
                    </span>
                  </Td>
                  <Td>
                    <div className="flex items-center gap-3">
                      <Avatar name={b.customer.name} />
                      <div>
                        <p className="m-0 text-sm font-medium text-[#111]">{b.customer.name}</p>
                        <p className="m-0 text-[11px] text-[#9CA3AF]">{carLabel(b.car)}</p>
                      </div>
                    </div>
                  </Td>
                  <Td>
                    <p className="m-0 text-sm text-[#111]">{b.service.category}</p>
                    <p className="m-0 text-[11px] text-[#9CA3AF]">{b.service.options[0].value}</p>
                  </Td>
                  <Td>{b.shop}</Td>
                  <Td><VisitsBadge visits={b.visits} /></Td>
                  <Td className="text-[#9CA3AF] text-xs">{b.updated}</Td>
                  <Td><StatusBadge status={b.status} /></Td>
                </tr>
              ))}
              {filteredBookings.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-sm text-[#9CA3AF]">
                    No {bookingFilter.toLowerCase()} chats right now.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </TableWrap>
        {filteredBookings.length > BOOKINGS_PREVIEW && (
          <button
            type="button"
            onClick={() => setShowAllBookings((v) => !v)}
            className="mt-4 w-full inline-flex items-center justify-center gap-1 rounded-xl border border-[#EEEEF1] py-2.5 text-xs font-semibold text-[#374151] hover:border-[#EA352B] hover:text-[#EA352B] transition"
          >
            {showAllBookings ? "Show less" : `Show all ${filteredBookings.length} chats`}
            <ChevronDown size={14} className={`transition-transform ${showAllBookings ? "rotate-180" : ""}`} />
          </button>
        )}
      </Card>

      <div className="flex flex-col gap-6 md:gap-4">
        <Card>
          <div className="flex items-center justify-between mb-4">
            <CardTitle>Pending Approvals</CardTitle>
            <LinkButton label="View all" to="/applications/pending" />
          </div>
          {approvals.length === 0 && (
            <div className="rounded-2xl bg-[#F7F7F9] p-5 text-center">
              <CheckCircle2 size={22} className="mx-auto text-[#16A34A]" />
              <p className="m-0 mt-2 text-sm text-[#6B7280]">All caught up — no pending providers.</p>
            </div>
          )}
          <div className="space-y-3">
            {approvals.map((p) => (
              <div key={p.id} className="flex items-center justify-between gap-3 rounded-2xl bg-[#F7F7F9] p-3">
                <div className="flex items-center gap-3 min-w-0">
                  <Avatar name={p.name} />
                  <div className="min-w-0">
                    <p className="m-0 text-sm font-medium text-[#111] truncate">{p.name}</p>
                    <p className="m-0 text-[11px] text-[#9CA3AF] truncate">{p.category} · {p.submitted}</p>
                  </div>
                </div>
                <div className="flex gap-1.5 shrink-0">
                  <button type="button" disabled={busyApproval === p.id} aria-label={`Approve ${p.name}`} title="Approve" onClick={() => handleApproval(p, true)} className="w-8 h-8 rounded-lg bg-[#DCFCE7] text-[#16A34A] flex items-center justify-center hover:scale-105 transition disabled:opacity-50 disabled:hover:scale-100">
                    <Check size={15} />
                  </button>
                  <button type="button" disabled={busyApproval === p.id} aria-label={`Reject ${p.name}`} title="Reject" onClick={() => handleApproval(p, false)} className="w-8 h-8 rounded-lg bg-[#FDECEA] text-[#EA352B] flex items-center justify-center hover:scale-105 transition disabled:opacity-50 disabled:hover:scale-100">
                    <X size={15} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card>
          <CardTitle>Recent Activity</CardTitle>
          <ol className="relative mt-4 mb-0 pl-0 list-none space-y-4">
            {data.recentActivity.map((a, i) => (
              <ActivityItem key={i} {...a} last={i === data.recentActivity.length - 1} />
            ))}
          </ol>
        </Card>
      </div>
    </section>
      <BookingDialog booking={openBooking} onClose={closeBooking} />
      <ShopDialog shop={openShop} onClose={closeShop} />
    </>
  );
}

// --- Helper Components ---

function Card({ children, className = "" }) {
  return (
    <div
      className={`min-w-0 bg-[#fff] rounded-3xl border border-[#EEEEF1] p-6 md:p-5 sm:p-4 transition-shadow hover:shadow-[0_8px_30px_rgba(17,17,26,0.06)] ${className}`}
      style={{ boxShadow: "0 1px 3px rgba(17,17,26,0.04)" }}
    >
      {children}
    </div>
  );
}

function CardTitle({ children }) {
  return (
    <h3 className="m-0 text-[1.25rem] sm:text-lg font-medium text-[#111]" style={FONT_HEADING}>
      {children}
    </h3>
  );
}

function IconBadge({ Icon }) {
  return (
    <span className="w-9 h-9 rounded-xl bg-[#FDECEA] flex items-center justify-center">
      <Icon size={17} className="text-[#EA352B]" />
    </span>
  );
}

function HeroStat({ label, value }) {
  return (
    <div className="rounded-2xl bg-[#ffffff14] border border-[#ffffff26] px-4 py-3 min-w-[110px] sm:min-w-0 sm:px-3 backdrop-blur-sm">
      <p className="m-0 text-[11px] text-[#ffffff99] uppercase tracking-wider">{label}</p>
      <p className="m-0 mt-1 text-lg sm:text-sm font-semibold whitespace-nowrap">{value}</p>
    </div>
  );
}

function StatCard({ Icon, label, value, change, spark, to }) {
  const positive = change >= 0;
  const sparkData = spark.map((v, i) => ({ i, v }));
  const color = positive ? BRAND.red : "#9CA3AF";
  const gradId = `spark-${label.replace(/\s/g, "")}`;

  const Wrapper = to ? Link : "div";
  return (
    <Wrapper
      {...(to ? { to } : {})}
      className="group no-underline bg-[#fff] rounded-3xl border border-[#EEEEF1] p-5 flex flex-col gap-3 transition-all hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgba(234,53,43,0.10)]"
      style={{ boxShadow: "0 1px 3px rgba(17,17,26,0.04)" }}
    >
      <div className="flex items-center justify-between">
        <span className="w-10 h-10 rounded-2xl bg-[#FDECEA] flex items-center justify-center transition-colors group-hover:bg-[#EA352B]">
          <Icon size={18} className="text-[#EA352B] group-hover:text-[#fff] transition-colors" />
        </span>
        <TrendPill value={change} />
      </div>
      <div>
        <p className="m-0 text-xs font-medium text-[#6B7280]">{label}</p>
        <p className="m-0 mt-1 text-2xl font-semibold text-[#111] whitespace-nowrap">{value}</p>
      </div>
      <div className="h-[36px] -mx-1">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={sparkData}>
            <defs>
              <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity={0.25} />
                <stop offset="100%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            </defs>
            <Area type="monotone" dataKey="v" stroke={color} strokeWidth={2} fill={`url(#${gradId})`} isAnimationActive={false} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </Wrapper>
  );
}

function TrendPill({ value }) {
  const positive = value >= 0;
  const Icon = positive ? TrendingUp : TrendingDown;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${
        positive ? "bg-[#DCFCE7] text-[#16A34A]" : "bg-[#FDECEA] text-[#EA352B]"
      }`}
    >
      <Icon size={12} />
      {positive ? "+" : ""}
      {value}%
    </span>
  );
}

function LegendDot({ color, label }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="w-2.5 h-2.5 rounded-full" style={{ background: color }} />
      {label}
    </span>
  );
}

function Segmented({ options, labels = {}, value, onChange }) {
  return (
    <div className="inline-flex rounded-xl bg-[#F3F3F6] p-1 shrink-0">
      {options.map((o) => (
        <button
          key={o}
          type="button"
          onClick={() => onChange(o)}
          className={`px-3.5 sm:px-2.5 py-1.5 text-xs font-semibold rounded-lg whitespace-nowrap transition-all ${
            value === o ? "bg-[#fff] text-[#EA352B] shadow-sm" : "text-[#6B7280] hover:text-[#111]"
          }`}
        >
          {labels[o] || o}
        </button>
      ))}
    </div>
  );
}

function ProgressRow({ label, value, max, color }) {
  const pct = Math.max(4, Math.round((value / (max || 1)) * 100));
  return (
    <div>
      <div className="flex items-center justify-between text-sm mb-1.5">
        <span className="text-[#374151]">{label}</span>
        <span className="font-semibold text-[#111]">{value.toLocaleString()}</span>
      </div>
      <div className="h-2 rounded-full bg-[#F1F1F4] overflow-hidden">
        <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}

function ChartTooltip({ active, payload, label, money, suffix = "", showTotal }) {
  if (!active || !payload?.length) return null;
  const total = payload.reduce((s, p) => s + Number(p.value || 0), 0);
  return (
    <div className="rounded-xl bg-[#1B1B22] text-[#fff] px-3 py-2 shadow-lg text-xs">
      {label !== undefined && <p className="m-0 mb-1 text-[#ffffff99]">{label}</p>}
      {payload.map((p) => (
        <p key={p.name} className="m-0 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: p.color || p.payload?.color || p.fill }} />
          {p.name}: <b>{money ? formatAED(p.value) : `${Number(p.value).toLocaleString()}${suffix}`}</b>
        </p>
      ))}
      {showTotal && payload.length > 1 && (
        <p className="m-0 mt-1 pt-1 border-t border-[#ffffff26]">
          Total: <b>{money ? formatAED(total) : total.toLocaleString()}</b>
        </p>
      )}
    </div>
  );
}

function TableWrap({ children }) {
  return <div className="overflow-x-auto -mx-6 md:-mx-5 sm:-mx-4 px-6 md:px-5 sm:px-4">{children}</div>;
}

function Th({ children }) {
  return (
    <th className="py-3 px-3 first:pl-0 text-[11px] font-semibold uppercase tracking-wider text-[#9CA3AF] border-b border-[#EEEEF1] whitespace-nowrap">
      {children}
    </th>
  );
}

function Td({ children, className = "" }) {
  return (
    <td className={`py-3 px-3 first:pl-0 text-sm text-[#4B5563] border-b border-[#F3F3F6] whitespace-nowrap ${className}`}>
      {children}
    </td>
  );
}

function LinkButton({ label, to }) {
  return (
    <Link to={to} className="shrink-0 no-underline inline-flex items-center gap-1 text-xs font-semibold text-[#EA352B] hover:text-[#C92A21] hover:gap-1.5 transition-all">
      {label} <ArrowUpRight size={14} />
    </Link>
  );
}

function SeriesToggle({ label, value, color, active, onClick }) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={active}
      onClick={onClick}
      className={`inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-left transition-all ${
        active ? "border-[#E4E4E9] bg-[#fff] shadow-sm" : "border-dashed border-[#E4E4E9] bg-[#F7F7F9] opacity-60"
      }`}
    >
      <span
        className="w-4 h-4 rounded-[5px] flex items-center justify-center border-2 transition-colors"
        style={{ borderColor: color, background: active ? color : "transparent" }}
      >
        {active && <Check size={10} strokeWidth={3} className="text-[#fff]" />}
      </span>
      <span>
        <span className="block text-[11px] text-[#6B7280] leading-none">{label}</span>
        <span className="block mt-1 text-xs font-semibold text-[#111] leading-none">{value}</span>
      </span>
    </button>
  );
}

function MiniStat({ Icon, label, value, sub }) {
  return (
    <div className="rounded-2xl bg-[#F7F7F9] p-3">
      <span className="flex items-center gap-1.5 text-[11px] text-[#6B7280]">
        <Icon size={13} className="text-[#EA352B] shrink-0" />
        <span className="truncate">{label}</span>
      </span>
      <span className="block mt-1.5 text-sm font-semibold text-[#111] truncate">{value}</span>
      {sub && <span className="block text-[11px] text-[#9CA3AF]">{sub}</span>}
    </div>
  );
}

function NotificationBell({ items }) {
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(true);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-label="Notifications"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={`relative w-10 h-10 rounded-xl bg-[#fff] border flex items-center justify-center transition ${
          open ? "border-[#EA352B]" : "border-[#ECECEF] hover:border-[#EA352B]"
        }`}
      >
        <Bell size={17} className="text-[#374151]" />
        {unread && <span className="absolute top-2 right-2 w-2 h-2 rounded-full bg-[#EA352B]" />}
      </button>
      {open && (
        <div className="absolute right-0 top-12 z-30 w-[340px] sm:w-[calc(100vw-24px)] rounded-2xl bg-[#fff] border border-[#EEEEF1] shadow-[0_16px_40px_rgba(17,17,26,0.12)] p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="m-0 text-sm font-semibold text-[#111]">Notifications</p>
            {unread && (
              <button type="button" onClick={() => setUnread(false)} className="text-[11px] font-semibold text-[#EA352B]">
                Mark all as read
              </button>
            )}
          </div>
          <ol className="m-0 pl-0 list-none space-y-3 max-h-[320px] overflow-y-auto">
            {items.map((a, i) => (
              <ActivityItem key={i} {...a} last />
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}

const ACTIVITY_ICONS = {
  subscription: { Icon: Crown, cls: "bg-[#FDECEA] text-[#EA352B]" },
  job: { Icon: CheckCircle2, cls: "bg-[#DCFCE7] text-[#16A34A]" },
  shop: { Icon: Store, cls: "bg-[#FFEDD5] text-[#EA580C]" },
  vehicle: { Icon: Car, cls: "bg-[#DBEAFE] text-[#2563EB]" },
  suspend: { Icon: UserMinus, cls: "bg-[#F3F4F6] text-[#374151]" },
  flag: { Icon: AlertTriangle, cls: "bg-[#FEF3C7] text-[#B45309]" },
};

function ActivityItem({ type, text, time, last }) {
  const { Icon, cls } = ACTIVITY_ICONS[type] || ACTIVITY_ICONS.job;
  return (
    <li className="relative flex gap-3">
      {!last && <span className="absolute left-[15px] top-9 bottom-[-16px] w-px bg-[#EEEEF1]" />}
      <span className={`w-8 h-8 rounded-lg shrink-0 flex items-center justify-center ${cls}`}>
        <Icon size={15} />
      </span>
      <div className="min-w-0">
        <p className="m-0 text-[13px] text-[#374151] leading-snug">{text}</p>
        <p className="m-0 mt-0.5 text-[11px] text-[#9CA3AF] inline-flex items-center gap-1">
          <Clock size={11} /> {time}
        </p>
      </div>
    </li>
  );
}
